import type { AiDraftFeedbackSubmission } from '@/components/aiDraft/feedback/types.ts';
import type { DraftFeedback } from '@/entities/enums/DraftFeedback.ts';
import type { DraftUnsupportedReason } from '@/api/aiDraft/types.ts';

export type EmailAiDraftLocale = 'fr' | 'en';

export const EmailAiDraftState = {
    Idle: 'idle',
    Loading: 'loading',
    Stopped: 'stopped',
    Failed: 'failed',
    RateLimited: 'rateLimited',
    Inserted: 'inserted',
    UpdateUnsupported: 'updateUnsupported',
} as const;

export type EmailAiDraftState = (typeof EmailAiDraftState)[keyof typeof EmailAiDraftState];

export interface EmailAiDraftSnapshot {
    subject: Record<EmailAiDraftLocale, string>;
    body: Record<EmailAiDraftLocale, string>;
}

export interface EmailAiDraftLocaleSnapshot {
    locale: EmailAiDraftLocale;
    subject: string;
    body: string;
}

export interface EmailAiDraftGeneratePayload {
    locale: EmailAiDraftLocale;
    leadId: number;
    requestId: string;
    existingSubject: string | null;
}

export interface EmailAiDraftUpdatePayload {
    locale: EmailAiDraftLocale;
    /** Plain text, NOT composer HTML — the composable strips markup before sending. */
    body: string;
    instruction: string;
    leadId: number;
    requestId: string;
    subject: string;
}

export interface EmailAiDraftStopPayload {
    leadId: number;
    requestId: string;
}

export interface EmailAiDraftCancelledResult {
    status: 'cancelled';
    interactionId: number;
    traceId: string | null;
}

export interface EmailAiDraftUnsupportedResult {
    status: 'unsupported';
    interactionId: number;
    traceId: string | null;
    reason: DraftUnsupportedReason;
}

export interface EmailAiDraftCompletedGenerateResult {
    subject: string;
    /** Composer-ready HTML; plain-text adapters must run it through `plainTextToComposerHtml` so blank lines survive zeroed paragraph margins. */
    body: string;
    interactionId: number;
    traceId: string | null;
}

export type EmailAiDraftGenerateResult =
    | EmailAiDraftCompletedGenerateResult
    | EmailAiDraftCancelledResult;

export interface EmailAiDraftCompletedUpdateResult {
    subject?: string;
    /** Composer-ready HTML. See {@link EmailAiDraftGenerateResult.body}. */
    body: string;
    interactionId: number;
    traceId: string | null;
}

export type EmailAiDraftUpdateResult =
    | EmailAiDraftCompletedUpdateResult
    | EmailAiDraftCancelledResult
    | EmailAiDraftUnsupportedResult;

export const isEmailAiDraftCancelledResult = (
    result: EmailAiDraftGenerateResult | EmailAiDraftUpdateResult
): result is EmailAiDraftCancelledResult => {
    return 'status' in result && result.status === 'cancelled';
};

export const isEmailAiDraftUnsupportedResult = (
    result: EmailAiDraftUpdateResult
): result is EmailAiDraftUnsupportedResult => {
    return 'status' in result && result.status === 'unsupported';
};

export interface EmailAiDraftGeneratedDraft extends EmailAiDraftCompletedGenerateResult {
    locale: EmailAiDraftLocale;
    useGeneratedSubject: boolean;
}

export type EmailAiDraftFeedbackSubmission = AiDraftFeedbackSubmission;

export interface EmailAiDraftFeedbackPayload extends EmailAiDraftFeedbackSubmission {
    interactionId: number;
    feedback: DraftFeedback;
}

export interface EmailAiDraftAdapter {
    generate(payload: EmailAiDraftGeneratePayload): Promise<EmailAiDraftGenerateResult>;
    update?(payload: EmailAiDraftUpdatePayload): Promise<EmailAiDraftUpdateResult>;
    stop(payload: EmailAiDraftStopPayload): Promise<void>;
    submitFeedback(payload: EmailAiDraftFeedbackPayload): Promise<void>;
}
